/**
 * demoTooltipContent.js
 *
 * Per-route tooltip content for demo and production guided hints.
 * Each entry:
 *   matchPath  — string or regex matched against window.location.pathname
 *   title      — short heading
 *   body       — guidance text (can include JSX if rendered via component)
 *   scope      — "demo" | "production" | "both"
 *                "demo"       → shown only when isDemoMode=true
 *                "production" → shown only in production (dashboards)
 *                "both"       → always shown
 */

const tooltipContent = [
  // ─── Public / Customer pages ──────────────────────────────────────────────
  {
    id: "home",
    matchPath: /^\/$/,
    title: "Welcome to the demo!",
    body: "Browse available salons and freelancers. Click any listing to view services and book an appointment.",
    scope: "demo",
  },
  {
    id: "shop-landing",
    matchPath: /^\/business\/[^/]+\/[^/]+$/,
    title: "Explore this salon",
    body: "Scroll down to see services, meet the team, and view their recent work. Click a service card to start booking.",
    scope: "demo",
  },
  {
    id: "business-locations",
    matchPath: /^\/business\/[^/]+$/,
    title: "Choose a location",
    body: "This business has multiple locations. Pick the branch that works for you, then browse services and book your appointment.",
    scope: "demo",
  },
  {
    id: "shop-catalogue",
    matchPath: /^\/business\/[^/]+\/[^/]+\/catalogue$/,
    title: "Browse products and packages",
    body: "Add products or packages to cart here, then go to checkout. Service bookings are managed from the services and booking pages.",
    scope: "demo",
  },
  {
    id: "freelancer-landing",
    matchPath: /^\/freelancer\//,
    title: "Book this professional",
    body: "Browse their services below. Select one to choose a date and time, then complete payment with the test card.",
    scope: "demo",
  },
  {
    id: "booking-select",
    matchPath: /^\/business\/[^/]+\/[^/]+\/(book|services)$/,
    title: "Choosing your appointment",
    body: "Pick a date and time that works for you. Available slots are shown in green. Tap a slot to continue.",
    scope: "demo",
  },
  {
    id: "checkout",
    matchPath: /^\/(checkout|cart)$/,
    title: "Completing your booking",
    body: "Enter your details, then pay with the test card below. Use a real email — confirmation emails are actually sent.",
    showCard: true,
    scope: "demo",
  },

  {
    id: "listing-pages",
    matchPath: /^\/(shops|freelancers|search|products|packages|catalogue)$/,
    title: "Find the right service",
    body: "Use filters and categories to narrow results. Open a business or freelancer profile to view services and start booking.",
    scope: "demo",
  },

  {
    id: "professional-profile",
    // covers /professional/:slug, /freelancer/:slug, and /business/:b/:l/professional/:slug
    matchPath: /^\/(professional|freelancer)\/[^/]+$|^\/business\/[^/]+\/[^/]+\/professional\/[^/]+$/,
    title: "Book this professional",
    body: "Review services, pricing, and availability. Select a service to pick a date and time, then complete checkout with the test card.",
    showCard: true,
    scope: "demo",
  },
  {
    id: "professional-services",
    matchPath: /^\/freelancer\/[^/]+\/services$/,
    title: "Select a service",
    body: "Choose a service to continue to scheduling and payment. Duration and pricing are shown on each card.",
    scope: "demo",
  },
  {
    id: "freelancer-booking",
    matchPath: /^\/freelancer-booking$/,
    title: "Finalize your booking",
    body: "Pick your date, time, and confirm your details. Use the test card below to pay.",
    showCard: true,
    scope: "demo",
  },
  {
    id: "booking-page",
    matchPath: /^\/booking$/,
    title: "Booking your appointment",
    body: "Select your preferred date and time. When you reach the payment step, use the test card below.",
    showCard: true,
    scope: "demo",
  },
  {
    id: "booking-confirmation",
    matchPath: /^\/booking-confirmation$/,
    title: "Booking confirmed!",
    body: "Your appointment has been booked. A confirmation email is on its way to the address you entered.",
    scope: "demo",
  },

  // ─── Staff / Owner dashboards ─────────────────────────────────────────────
  {
    id: "owner-dashboard",
    matchPath: /^\/dashboard\/(owner|business)/,
    title: "Your business hub",
    body: "Manage bookings, staff, services, and earnings from the sidebar menu. Use the calendar to get a bird's-eye view of today.",
    scope: "both",
  },
  {
    id: "professional-dashboard",
    matchPath: /^\/dashboard\/professional/,
    title: "Your professional dashboard",
    body: "View your upcoming appointments, manage your availability, and track your earnings here.",
    scope: "both",
  },
  {
    id: "freelancer-dashboard",
    matchPath: /^\/dashboard\/freelancer/,
    title: "Your freelancer dashboard",
    body: "Control your schedule, services, and client bookings. Set your travel radius and service areas under Settings.",
    scope: "both",
  },
  {
    id: "staff-bookings",
    matchPath: /\/dashboard\/.+\/bookings/,
    title: "Managing bookings",
    body: "All appointments are listed here. Use the status filters to view pending, confirmed, or completed bookings.",
    scope: "both",
  },
  {
    id: "staff-services",
    matchPath: /\/dashboard\/.+\/services/,
    title: "Your service menu",
    body: "Add, edit, or deactivate services from here. Prices and durations you set will appear immediately on your public profile.",
    scope: "both",
  },
  {
    id: "staff-settings",
    matchPath: /\/dashboard\/.+\/settings/,
    title: "Account & shop settings",
    body: "Update your business details, notification preferences, payout method, and availability hours from this page.",
    scope: "both",
  },
];

/**
 * getTooltipForPath
 * Returns the best matching tooltip for the given pathname and isDemoMode flag.
 */
export function getTooltipForPath(pathname, isDemoMode) {
  for (const item of tooltipContent) {
    const matches =
      typeof item.matchPath === "string"
        ? pathname === item.matchPath
        : item.matchPath.test(pathname);

    if (!matches) continue;

    if (item.scope === "demo" && !isDemoMode) continue;
    if (item.scope === "production" && isDemoMode) continue;

    return item;
  }
  return null;
}

export default tooltipContent;
